<template>
    <v-row>
        <v-col cols="12" sm="12" md="2" class="pa-2 left-column">
            <v-card class="teams-card">
                <v-card-text class="pa-2">
                    <v-text-field v-model="listSearchQuery"
                                  :label="$t('searchTeams')"
                                  hide-details
                                  density="compact"
                                  variant="outlined"
                                  prepend-inner-icon="mdi-magnify"
                                  class="mb-2"></v-text-field>
                </v-card-text>
                <v-expansion-panels v-model="openPanels" multiple>
                    <v-expansion-panel v-for="(profileTeams, profile) in filteredGroupedTeams"
                                       :key="profile"
                                       class="team-panel">
                        <v-expansion-panel-title density="compact">
                            <span class="text-subtitle-2">{{ profile }}</span>
                            <template v-slot:actions>
                                <v-chip size="small" color="primary" text-color="white">
                                    {{ getTotalProfileCount(profileTeams) }}
                                </v-chip>
                            </template>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-list v-model:selected="selectedTeam" density="compact">
                                <v-list-item v-for="team in profileTeams"
                                             :key="team.id"
                                             :value="team.id"
                                             @click="selectTeamInFilterContext(team.id)"
                                             class="team-list-item">
                                    <v-list-item-title class="text-body-2">{{ team.name }}</v-list-item-title>
                                    <template v-slot:append>
                                        <v-chip size="small" text-color="white">
                                            {{ getOriginalTeamCount(team.id) }}
                                        </v-chip>
                                    </template>
                                </v-list-item>
                            </v-list>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-card>
        </v-col>

        <v-divider vertical class="d-none d-md-flex" />

        <v-col cols="12" md="10" class="pa-2 right-column">
            <v-sheet elevation="2" rounded class="mb-4 pa-4">
                <v-row no-gutters align="center">
                    <v-col cols="12" sm="6" md="4" class="d-flex align-center">
                        <v-btn class="mr-2"
                               :disabled="selectedSubItems.length === 0"
                               prepend-icon="mdi-account-multiple-outline"
                               variant="outlined"
                               @click="openAssignmentDialog"
                               density="comfortable">
                            {{ $t('assign', { count: selectedSubItems.length }) }}
                        </v-btn>
                        <v-tooltip v-if="selectedTeam[0] !== t('unAssigned')" :text="$t('tooltips.selectUnassigned')" location="bottom">
                            <template v-slot:activator="{ props }">
                                <v-btn variant="text" v-bind="props" @click="selectUnassignedUsers" density="comfortable">
                                    <v-icon>mdi-account-multiple-plus</v-icon>
                                </v-btn>
                            </template>
                        </v-tooltip>

                        <v-tooltip :text="$t('tooltips.selectAll')" location="bottom">
                            <template v-slot:activator="{ props }">
                                <v-btn variant="text" v-bind="props" @click="selectAll" density="comfortable">
                                    <v-icon>mdi-select-all</v-icon>
                                </v-btn>
                            </template>
                        </v-tooltip>

                        <v-tooltip :text="$t('tooltips.deselectAll')" location="bottom">
                            <template v-slot:activator="{ props }">
                                <v-btn variant="text" v-bind="props" @click="deselectAll" density="comfortable">
                                    <v-icon>mdi-select-off</v-icon>
                                </v-btn>
                            </template>
                        </v-tooltip>
                    </v-col>
                    <v-col cols="12" sm="6" md="8" class="d-flex justify-end">
                        <v-text-field v-model="searchQuery"
                                      :label="$t('search')"
                                      hide-details
                                      density="compact"
                                      variant="outlined"
                                      prepend-inner-icon="mdi-magnify"
                                      class="search-field"
                                      style="max-width: 300px;"></v-text-field>
                    </v-col>
                </v-row>
            </v-sheet>

            <v-sheet elevation="2" rounded class="pa-4">
                <v-data-table v-model="selectedSubItems"
                              :headers="headers"
                              :items="filteredItems"
                              item-value="subItems[0].id"
                              show-select
                              class="elevation-0"
                              :sort-by="[{ key: 'itemUpdated', order: 'desc' }]"
                              :expanded="expanded"
                              item-key="id"
                              :expand-on-click="false"
                              :locale="$i18n.locale"
                              :no-data-text="$t('noData')"
                              :items-per-page-text="$t('itemsPerPage')"
                              :hide-headers="isMobile">

                    <!-- Mobile Layout -->
                    <template v-if="isMobile" v-slot:item="{ item }">
                        <tr>
                            <td>
                                <v-card class="mb-2" variant="flat">
                                    <v-card-title class="d-flex justify-space-between align-center">
                                        <v-checkbox v-if="item.subItems.length === 1"
                                                    v-model="selectedSubItems"
                                                    :value="item.subItems[0].id"
                                                    hide-details
                                                    class="mr-2"></v-checkbox>
                                        <v-chip label variant="text">
                                            <v-icon v-bind="getIconClass(getCategory(getItemTemplate(item.profileId).categoryId).icon)" class="mr-2"></v-icon>
                                            <a @click.prevent="openUrl(`https://${settings.apiUrl.startsWith('api-') ? settings.apiUrl.slice(4) : settings.apiUrl}/${settings.projectCode}/${item.type === 'IssueTemplate' ? 'issues?issue' : 'processes?process'}-client-ids=${item.clientId}&subcategory-ids=${getItemTemplate(item.profileId).subcategoryId}`)"
                                               href="#"
                                               style="color: inherit;">
                                                {{ item.clientId }}
                                            </a>
                                        </v-chip>
                                        <v-chip :style="{
                                            backgroundColor: getStatusTemplate(item.subItems[0].statusId).color,
                                            color: 'white',
                                            marginTop:'5px'
                                          }"
                                          label>
                                            {{ getStatusTemplate(item.subItems[0].statusId).title }}
                                        </v-chip>
                                    </v-card-title>

                                    <v-card-text>
                                        <div class="mb-2 text-h6">
                                            {{ getItemTemplate(item.profileId).title }}
                                        </div>

                                        <div v-if="item.subItems.length === 1" class="mb-2 d-flex justify-space-between">
                                            <strong style="flex-shrink: 0">{{ $t('assignedTo') }}:</strong>
                                            <div class="d-flex flex-column align-end">
                                                <span>{{ item.subItems[0].assigneeTeamId ? getTeam(item.subItems[0].assigneeTeamId).name : '-' }}</span>
                                                <div class="d-flex justify-end mt-1">
                                                    <template v-if="item.subItems[0].assigneeUserIds.length > 0">
                                                        <v-avatar v-for="userId in item.subItems[0].assigneeUserIds.slice(0, 3)"
                                                                  :key="userId"
                                                                  size="28"
                                                                  class="mr-1">
                                                            <v-img v-if="getUser(userId).avatarUrl"
                                                                   :src="getAvatarUrl(getUser(userId).avatarUrl)"
                                                                   alt="User Avatar"></v-img>
                                                            <span v-else
                                                                  class="d-flex justify-center align-center text-caption white--text"
                                                                  :style="{
                                                                    backgroundColor: getRandomColor(userId),
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    borderRadius: '50%'
                                                                  }">
                                                                {{ getInitials(getUser(userId).displayName) }}
                                                            </span>
                                                        </v-avatar>
                                                        <span v-if="item.subItems[0].assigneeUserIds.length > 3" class="ml-1">
                                                            +{{ item.subItems[0].assigneeUserIds.length - 3 }}
                                                        </span>
                                                    </template>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="mb-2 d-flex justify-space-between">
                                            <strong style="flex-shrink: 0">{{ $t('location') }}:</strong>
                                            <div class="d-flex flex-column align-end" style="max-width: calc(100% - 80px);">
                                                <div class="w-100 d-flex justify-end">
                                                    <div class="d-flex align-start text-right">
                                                        <v-icon icon="mdi-map" size="small" class="flex-shrink-0 mr-1 mt-1"></v-icon>
                                                        <a @click.prevent="openUrl(`https://${settings.apiUrl.startsWith('api-') ? settings.apiUrl.slice(4) : settings.apiUrl}/${settings.projectCode}/plan-viewer/${getLocation(item.locationId).drawingId}`)"
                                                           href="#"
                                                           style="color: inherit;">
                                                            {{ getLocation(item.locationId).drawingTitle }}
                                                        </a>
                                                    </div>
                                                </div>
                                                <div class="w-100 d-flex justify-end mt-1">
                                                    <div class="d-flex align-start text-right">
                                                        <v-icon icon="mdi-map-marker" size="small" class="flex-shrink-0 mr-1 mt-1"></v-icon>
                                                        <a @click.prevent="openUrl(`https://${settings.apiUrl.startsWith('api-') ? settings.apiUrl.slice(4) : settings.apiUrl}/${settings.projectCode}/plan-viewer/${getLocation(item.locationId).drawingId}#view/location${getLocation(item.locationId).id}`)"
                                                           href="#"
                                                           style="color: inherit;">
                                                            {{ getLocation(item.locationId).title }}
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="mb-2 d-flex justify-space-between">
                                            <strong style="flex-shrink: 0">{{ $t('updatedDate') }}:</strong>
                                            <div class="d-flex align-center" :style="{
                                                'text-align': item.subItems.length > 1 ? 'center' : 'right',
                                                'justify-content': item.subItems.length > 1 ? 'center' : 'flex-end'
                                            }">
                                                {{ formatDate(item.subItems[0].statusCreatedDate) }}
                                                <template v-if="item.subItems.length > 1">
                                                    <v-btn class="ml-2"
                                                           prepend-icon="mdi-file-document-outline"
                                                           :text="item.subItems.length.toString()"
                                                           variant="outlined"
                                                           @click="showSubItemsDialog = true; selectedSubItems = item"></v-btn>
                                                </template>
                                            </div>
                                        </div>
                                    </v-card-text>
                                </v-card>
                            </td>
                        </tr>
                    </template>

                    <!-- Desktop mode-->
          <template v-else v-slot:item="{ item, columns }">
            <tr>
              <td class="client-id-cell">
                <div class="d-flex align-center">
                  <template v-if="item.subItems.length > 1">
                    <v-btn
                      :icon="expandIcon(item)"
                      @click="toggleExpand(item)"
                      variant="plain"
                      class="mr-2"
                    ></v-btn>
                  </template>
                  <v-checkbox
                    v-if="item.subItems.length === 1"
                    v-model="selectedSubItems"
                    :value="item.subItems[0].id"
                    hide-details
                    class="mr-2"
                  ></v-checkbox>
                  <v-chip label variant="text">
                    <v-icon v-bind="getIconClass(getCategory(getItemTemplate(item.profileId).categoryId).icon)" class="mr-2"></v-icon>
                    <a
                      @click.prevent="openUrl(`https://${settings.apiUrl.startsWith('api-') ? settings.apiUrl.slice(4) : settings.apiUrl}/${settings.projectCode}/${item.type === 'IssueTemplate' ? 'issues?issue' : 'processes?process'}-client-ids=${item.clientId}&subcategory-ids=${getItemTemplate(item.profileId).subcategoryId}`)"
                      href="#"
                      style="color: inherit;"
                    >
                      {{ item.clientId }}
                    </a>
                  </v-chip>
                </div>
              </td>
              <td>
                <div class="d-flex flex-column align-start">
                  <template v-if="item.subItems.length === 1">
                    <span class="text-start">{{ getItemTemplate(item.profileId).title }}</span>
                    <span style="color: #888;" class="text-start text-caption">
                      {{ getFormTemplateTitleIfDifferent(item.subItems[0].itemTemplateId, item.profileId) }}
                    </span>
                  </template>
                  <template v-else>
                    <span class="text-start">{{ getItemTemplate(item.profileId).title }}</span>
                  </template>
                </div>
              </td>
              <td>
                <div class="d-flex align-center">
                  <template v-if="hasAssignees(item)">
                    <v-tooltip
                      v-for="userId in getUniqueAssigneeUserIds(item)"
                      :key="userId"
                      :text="getUser(userId).displayName || $t('unknownUser')"
                      location="bottom"
                    >
                      <template v-slot:activator="{ props }">
                        <v-avatar v-bind="props" :size="28" class="mr-1">
                          <v-img
                            v-if="getUser(userId).avatarUrl"
                            :src="getAvatarUrl(getUser(userId).avatarUrl)"
                            alt="User Avatar"
                          ></v-img>
                          <span
                            v-else
                            class="d-flex justify-center align-center text-caption white--text"
                            :style="{
                              backgroundColor: getRandomColor(userId),
                              width: '100%',
                              height: '100%',
                              borderRadius: '50%'
                            }">
                            {{ getInitials(getUser(userId).displayName) }}
                          </span>
                        </v-avatar>
                      </template>
                    </v-tooltip>
                    <span v-if="getUniqueAssigneeUserIds(item).length > 5" class="ml-1">
                      +{{ getUniqueAssigneeUserIds(item).length - 5 }}
                    </span>
                  </template>
                  <span v-else>-</span>
                </div>
              </td>
              <td>
                <div>
                  <v-icon icon="mdi-map" size="small" class="mr-1"></v-icon>
                  <a @click.prevent="openUrl(`https://${settings.apiUrl.startsWith('api-') ? settings.apiUrl.slice(4) : settings.apiUrl}/${settings.projectCode}/plan-viewer/${getLocation(item.locationId).drawingId}`)"
                     href="#"
                     style="color: inherit;">
                    {{ getLocation(item.locationId).drawingTitle }}
                  </a>
                </div>
                <div>
                  <v-icon icon="mdi-map-marker" size="small" class="mr-1"></v-icon>
                  <a @click.prevent="openUrl(`https://${settings.apiUrl.startsWith('api-') ? settings.apiUrl.slice(4) : settings.apiUrl}/${settings.projectCode}/plan-viewer/${getLocation(item.locationId).drawingId}#view/location${getLocation(item.locationId).id}`)"
                     href="#"
                     style="color: inherit;">
                    {{ getLocation(item.locationId).title }}
                  </a>
                </div>
              </td>
              <td>{{ formatDate(item.subItems[0].statusCreatedDate) }}</td>
              <td>
                <v-chip :style="{
                    backgroundColor: getStatusTemplate(item.subItems[0].statusId).color,
                    color: 'white'
                  }"
                  label
                  class="v-chipStatus"
                >
                  {{ getStatusTemplate(item.subItems[0].statusId).title }}
                </v-chip>
              </td>
            </tr>
            <tr v-if="isExpanded(item) && item.subItems.length > 1">
              <td :colspan="columns.length">
                <v-data-table
                  v-model="selectedSubItems"
                  :headers="[
                    { title: $t('headers.subItem'), key: 'itemTemplateId', align: 'start', sortable: true },
                    { title: $t('headers.assignedTo'), key: 'assigneeUserIds', align: 'start', sortable: false },
                    { title: $t('headers.updatedDate'), key: 'updatedDate', align: 'start', sortable: true },
                    { title: $t('headers.status'), key: 'statusId', align: 'start', sortable: true }
                  ]"
                  :items="item.subItems"
                  item-value="id"
                  show-select
                  hide-default-footer
                  class="elevation-0"
                >
                  <template #[`item.itemTemplateId`]="{ item: subItem }">
                    {{ getFormTemplateTitle(subItem.itemTemplateId) }}
                  </template>

                  <template #[`item.assigneeUserIds`]="{ item: subItem }">
                    <div class="d-flex align-center">
                      <template v-if="subItem.assigneeUserIds.length > 0">
                        <v-tooltip v-for="userId in subItem.assigneeUserIds.slice(0, 5)"
                                   :key="userId"
                                   :text="getUser(userId).displayName || $t('unknownUser')"
                                   location="bottom">
                          <template v-slot:activator="{ props }">
                            <v-avatar v-bind="props" :size="28" class="mr-1">
                              <v-img v-if="getUser(userId).avatarUrl"
                                     :src="getAvatarUrl(getUser(userId).avatarUrl)"
                                     alt="User Avatar"></v-img>
                              <span v-else
                                    class="d-flex justify-center align-center text-caption white--text"
                                    :style="{
                                      backgroundColor: getRandomColor(userId),
                                      width: '100%',
                                      height: '100%',
                                      borderRadius: '50%'
                                    }">
                                {{ getInitials(getUser(userId).displayName) }}
                              </span>
                            </v-avatar>
                          </template>
                        </v-tooltip>
                        <span v-if="subItem.assigneeUserIds.length > 5" class="ml-1">
                          +{{ subItem.assigneeUserIds.length - 5 }}
                        </span>
                      </template>
                      <span v-else>-</span>
                    </div>
                  </template>

                  <template #[`item.updatedDate`]="{ item: subItem }">
                    {{ formatDate(subItem.statusCreatedDate) }}
                  </template>

                  <template #[`item.statusId`]="{ item: subItem }">
                    <v-chip :style="{
                        backgroundColor: getStatusTemplate(subItem.statusId).color,
                        color: 'white'
                      }"
                      label>
                      {{ getStatusTemplate(subItem.statusId).title }}
                    </v-chip>
                  </template>
                </v-data-table>
                            </td>
                        </tr>
                    </template>
                    <template v-slot:headers="{}">
                        <tr v-if="!isMobile">
                            <th v-for="header in headers" :key="header.key">
                                {{ header.title }}
                            </th>
                        </tr>
                    </template>
                </v-data-table>
            </v-sheet>
        </v-col>
    </v-row>
    <TeamAssignmentDialog v-model="showAssignmentDialog"
                          v-model:selectedSubItems="selectedSubItems"
                          v-model:selectedTeam="selectedTeam"
                          :teamUsers="teamUsers"
                          :allItems="allItems"
                          :assigning="assigning"
                          @assign="handleAssign" />
</template>

<script src="./model.js"></script>
<style src="./style.css" scoped></style>
